<template>
  <div class="flex flex-col items-start gap-1" data-testid="document-status-badge">
    <LabelBadge :label="config.label" :color="config.color" :icon="config.icon" size="small" />
    <span v-if="reasonText" class="text-[11px] leading-4 text-negative text-left">
      {{ reasonText }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAlertCircleOutline, mdiCheckCircle, mdiClockTimeThree } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import type {
  DocumentRejection,
  ProfessionalDocumentPendingReason
} from '@/types/professional-documents.types'

import { DOCUMENT_STATUS_LABEL, pendingReasonText } from './labels'

const props = withDefaults(
  defineProps<{
    status: ProfessionalDocumentStatus
    pendingReason?: ProfessionalDocumentPendingReason | null
    rejection?: DocumentRejection | null
    expiresAt?: string | null
    isLegacy?: boolean
  }>(),
  { pendingReason: null, rejection: null, expiresAt: null, isLegacy: false }
)

const VISUAL: Record<ProfessionalDocumentStatus, { color: LabelBadgeColor; icon: string }> = {
  [ProfessionalDocumentStatus.PENDING]: { color: 'red', icon: mdiAlertCircleOutline },
  [ProfessionalDocumentStatus.IN_REVIEW]: { color: 'yellow', icon: mdiClockTimeThree },
  [ProfessionalDocumentStatus.CONCLUDED]: { color: 'green', icon: mdiCheckCircle }
}

const config = computed(() => ({
  ...VISUAL[props.status],
  label:
    props.isLegacy && props.status === ProfessionalDocumentStatus.CONCLUDED
      ? 'Concluído (legado)'
      : DOCUMENT_STATUS_LABEL[props.status]
}))

const reasonText = computed(() =>
  pendingReasonText({
    status: props.status,
    pendingReason: props.pendingReason,
    rejection: props.rejection,
    expiresAt: props.expiresAt
  })
)
</script>
